import { AfterViewInit, Component, OnDestroy, ViewChild } from "@angular/core";
import { DialogService } from "src/app/dialog/dialog.service";
import { DialogComponent } from "src/app/dialog/dialog/dialog.component";
import { LoggerService } from "src/app/telemetry/logger.service";
import { TelemetryIdentifiers } from "src/app/telemetry/telemetry-identifiers";
import { environment } from "src/environments/environment";
import { NetworkConnectivityService } from "src/app/network-connectivity.service";
import { timer, takeUntil, Subject, take } from "rxjs";
import { VersionUpdateService } from "src/app/versioning/version-update-service";
import { ActivatedRoute, Router } from "@angular/router";

/**
 * The component for the version updates.
 */
@Component({
	selector: 'crownx-version-upgrade',
	templateUrl: './version-upgrade.component.html',
	styleUrls: ['./version-upgrade.component.css']
})
export class VersionUpgradeComponent implements AfterViewInit, OnDestroy {

	/** Interval (in milliseconds) for checks if a new version is available. */
	private static readonly CHECK_FOR_VERSION_INTERVAL: number = 60000;

	/** Interval (in milliseconds) to see if the user needs to be re-prompted to upgrade. */
	private static readonly CHECK_FOR_UPGRADE_REPROMPT_INTERVAL: number = 120000;

	/** Indicates whether a loading operation is in progress when performing a version update. */
	private isPerformingUpdate: boolean = false;

	/** A boolean to track if the version update was successful. */
	public IsVersionUpdateSuccess: boolean = false;

	/** The client version. */
	public clientVersion: string = '';

	/** A reference to the dialog for displaying the version update status. */
	@ViewChild("VersionUpdateStatusDialog")
	public VersionUpdateStatusDialog!: DialogComponent;

	/** A reference to the dialog for displaying the new version detection. */
	@ViewChild("VersionDetectionPromptDialog")
	public VersionDetectionPromptDialog!: DialogComponent;

	/** Used to unsubscribe from observables. */
	private unsubscriber = new Subject();

	/** A boolean to indicate if the user is able to postpone */
	private isPostponeEnabled: boolean = true;

	/** A lifecycle hook that is called after Angular has fully initialized a component's view. */
	public ngAfterViewInit(): void {
		this.LogVersionUpdateStatus();
	}

	/**
	 * A lifecycle hook that is called when the component is initialized.
	 */
	public ngOnInit(): void {
		timer(0, VersionUpgradeComponent.CHECK_FOR_VERSION_INTERVAL).pipe(takeUntil(this.unsubscriber)).subscribe(async () => {
			const isVersionAvailable: boolean = await this.versionUpdateService.CheckForUpdatesOnly();
			if (isVersionAvailable && !this.IsUpgradePromptSuppressed()) {
				this.dialogService.ShowModalDialog(this.VersionDetectionPromptDialog);
			}
		});
		this.RepromptOnInterval();
		this.PromptOnEvent();
	}

	/**
	 * Component destruction.
	 */
	public ngOnDestroy(): void {
		this.unsubscriber.next(null);
		this.unsubscriber.complete();
	}

	/**
	 * @constructor
	 *
	 * @param versionUpdateService The service used for checking and initiating client updates when an update is available
	 * @param dialogService The dialog service.
	 * @param logger The logging service.
	 * @param networkConnectivityService The service used to see if the device has network connection.
	 * @param router The Angular router.
	 * @param activatedRoute The root ActivatedRoute, walked to find routes that opt out of the global upgrade prompt via `data.SuppressUpgradePrompt`.
	 */
	constructor(
		private readonly versionUpdateService: VersionUpdateService,
		private readonly dialogService: DialogService,
		private readonly logger: LoggerService,
		private readonly networkConnectivityService: NetworkConnectivityService,
		private readonly router: Router,
		private readonly activatedRoute: ActivatedRoute
	) { }

	/**
	 * Compares the current version from the URL query string with the latest version
	 * and logs telemetry metrics and events based on whether the update was successful.
	 */
	public LogVersionUpdateStatus(): void {
		this.clientVersion= `${environment.Version}`;
		const queryParams: URLSearchParams = new URLSearchParams(window.location.search);
		const previousClientVersion: string = queryParams.get('version')!;
		const startTimeParam: string | null = queryParams.get('upgradeStartTime');

		if (!previousClientVersion || !startTimeParam) {
			return;
		}

		const updateVersionStartDate: Date = new Date(decodeURIComponent(startTimeParam));
		const updateVersionEndDate: Date = new Date();
		const operationMilliseconds: number = updateVersionEndDate.getTime() - updateVersionStartDate.getTime();

		if (this.clientVersion != previousClientVersion) {
			this.IsVersionUpdateSuccess = true;
			this.dialogService.ShowModalDialog(this.VersionUpdateStatusDialog);
			this.logger.TrackMetric(TelemetryIdentifiers.METRIC_VERSION_UPDATE_SUCCESSFUL, operationMilliseconds);
			this.logger.TrackEvent(TelemetryIdentifiers.EVENT_VERSION_UPDATE_SUCCESSFUL);
		}
		else {
			this.IsVersionUpdateSuccess = false;
			this.dialogService.ShowModalDialog(this.VersionUpdateStatusDialog);
			this.logger.TrackMetric(TelemetryIdentifiers.METRIC_VERSION_UPDATE_FAILED, operationMilliseconds);
			this.logger.TrackEvent(TelemetryIdentifiers.EVENT_VERSION_UPDATE_FAILED);
		}

		// Remove query params
		this.router.navigate([], {
			queryParams: {
				'version': null,
				'upgradeStartTime': null,
			},
			queryParamsHandling: 'merge'
		});
	}

	/**
	 * Event handler for when upgrade button is clicked on version detection dialog.
	 */
	public async OnVersionUpgradeButtonClick(): Promise<void> {
		this.isPerformingUpdate = true;
		this.logger.TrackEvent(TelemetryIdentifiers.EVENT_CONFIRM_VERSION_UPDATE);
		try {
			await this.versionUpdateService.CheckForUpdatesAndPerformUpdate();
		}
		catch (error: any) {
			this.logger.TrackError('Error during manual version update check', error);
		}
		finally {
			this.isPerformingUpdate = false;
			this.isPostponeEnabled = true;
		}
	}

	/**
	 * Event handler for when postpone button is clicked on version detection dialog.
	 */
	public OnPostponeButtonClick(): void {
		this.logger.TrackEvent(TelemetryIdentifiers.EVENT_POSTPONE_VERSION_UPDATE);
		this.dialogService.HideModalDialog(this.VersionDetectionPromptDialog);
		this.versionUpdateService.RecordPostponedUpdateTime();
	}

	/**
	 * Event handler for when postpone button is clicked on version detection dialog.
	 */
	public OnVersionUpdateStatusDialogOkayClick(): void {
		this.dialogService.HideModalDialog(this.VersionUpdateStatusDialog);
	}

	/**
	 * Indicates if the postpone button should be disabled
	 * @returns True if the upgrade is currently happening or if postponing isn't possible
	 * @readonly
	 */
	public get IsPostponeButtonDisabled(): boolean {
		return !this.isPostponeEnabled || this.isPerformingUpdate;
	}

	/**
	 * Indicates if the upgrade button should be disabled
	 * @returns True if the upgrade is currently being downloaded
	 * @readonly
	 */
	public get IsUpgradeButtonLoading(): boolean {
		return this.versionUpdateService.IsCheckingForUpdate || this.isPerformingUpdate;
	}

	/**
	 * Gets the text the upgrade button should display while loading
	 * @returns The text to be displayed. "Upgrading" if upgrading. "Downloading" if downloading upgrade.
	 * @readonly
	 */
	public get UpgradeButtonLoadingText(): string {
		if (this.isPerformingUpdate)
			return "Upgrading";
		return "Downloading";
	}

	/**
	 * Checks on an interval to see if the user needs to be re-prompted to update the application
	 */
	private RepromptOnInterval(): void {
		timer(0, VersionUpgradeComponent.CHECK_FOR_UPGRADE_REPROMPT_INTERVAL).pipe(takeUntil(this.unsubscriber)).subscribe(() => {
			this.isPostponeEnabled = true;
			if (this.versionUpdateService.IsPostponeOld && this.networkConnectivityService.IsOnline) {
				this.versionUpdateService.IsUpdateNeeded$().pipe(take(1)).subscribe((isUpdateStillNeeded: boolean) => {
					if (isUpdateStillNeeded) {
						this.DisplayPromptAndLogEvent(TelemetryIdentifiers.EVENT_REPROMPT_FOR_VERSION_UPDATE);
					}
				});
			}
		});
	}

	/**
	 * Subscribes to the event to prompt a user to upgrade.
	 */
	private PromptOnEvent(): void {
		this.versionUpdateService.TriggerUpgradePromptEvent.pipe(takeUntil(this.unsubscriber)).subscribe((parameters: {allowPostpone: boolean, verifyUpdate: boolean, telemetryIdentifier: string}) => {
			this.ShowPrompt(parameters.allowPostpone, parameters.verifyUpdate, parameters.telemetryIdentifier);
		});
	}

	/**
	 * Prompt the user to update.
	 * @param allowPostpone A boolean indicating if the postpone option should be allowed on the dialog
	 * @param verifyUpdate A boolean to indicate if the update needs to be verified
	 * @param telemetryIdentifier The telemetry Identifier of the event that is triggering the prompt
	 */
	private async ShowPrompt(allowPostpone: boolean, verifyUpdate: boolean, telemetryIdentifier: string): Promise<void> {
		this.isPostponeEnabled = allowPostpone;
		if (!this.networkConnectivityService.IsOnline)
			return;

		// if we know there is an update waiting (postponed), the service worker will have the update ready for download.
		// if there isn't an update waiting and the update has already been verified, we want to check with the service worker to see if there is an update that needs to be downloaded
		if (this.versionUpdateService.IsUpdateWaiting) {
			// if we still need to verify the update, we should make a call to the api to ensure the client still needs to update
			if (verifyUpdate)
			{
				this.versionUpdateService.IsUpdateNeeded$().pipe(take(1)).subscribe((isUpdateStillNeeded: boolean) => {
					if (isUpdateStillNeeded)
					{
						this.DisplayPromptAndLogEvent(telemetryIdentifier);
					}
				});
			}
			else
			{
				this.DisplayPromptAndLogEvent(telemetryIdentifier);
			}
		}
		else if (!verifyUpdate) {
			this.DisplayPromptAndLogEvent(telemetryIdentifier);
			this.versionUpdateService.CheckForUpdatesOnly();
		}
	}

	/**
	 * Shows the Version Detection Prompt and logs an event
	 * @param telemetryIdentifier The telemetry event to log
	 */
	private DisplayPromptAndLogEvent(telemetryIdentifier: string): void {
		// Some routes (eg. the driver End-of-Route page) render their own inline upgrade ui and opt out of the global prompt via SuppressUpgradePrompt on their route declaration.
		if (this.IsUpgradePromptSuppressed()) {
			return;
		}
		this.dialogService.ShowModalDialog(this.VersionDetectionPromptDialog);
		this.logger.TrackEvent(telemetryIdentifier);
	}

	/**
	 * Walks the activated-route tree looking for any route that has opted out of the global upgrade prompt
	 * via `data: { SuppressUpgradePrompt: true }`. The flag may be set on the leaf or any ancestor, so the entire chain is inspected.
	 * @returns True if any active route in the current chain declares `SuppressUpgradePrompt`.
	 */
	private IsUpgradePromptSuppressed(): boolean {
		let route: ActivatedRoute | null = this.activatedRoute.root;
		while (route) {
			if (route.snapshot.data['SuppressUpgradePrompt'] === true) {
				return true;
			}
			route = route.firstChild;
		}
		return false;
	}
}